<script setup lang="ts">
import type { FormErrorEvent, FormSubmitEvent } from '#ui/types';
import { phoneValidator } from '@lib/shared';
import { z } from 'zod';

import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import { type BackendErrorEntity, type ErrorType, LeadIntent, type PostLeadBodyDto, SellingMethod, useApiPostLead } from '~/apiClient';
import type { SaleItemEntity } from '~/types/sale.type';

export type LeadModalProps = {
    sale: SaleItemEntity;
    intentType: LeadIntent;
};

// PROPS
const { sale, intentType } = defineProps<LeadModalProps>();

// MODEL
const open = defineModel<boolean>();

// EMITS
const emit = defineEmits<{
    close: [CustomerLeadContacts | undefined];
}>();

// COMPOSABLES
const { mutateAsync: mutateSubmit, isPending: isSubmitting } = useApiPostLead();
const { logError, logInfo } = useLogs();
const { isLgOrGreater, isMobile } = useResponsive();
const route = useRoute();
const { tt, locale } = useTypedI18n();
const { data: session } = useAuthUtils();
const { salesContact, suspense: suspenseSalesContact } = useSalesContact({ sale: () => sale });
await suspenseSalesContact();

// DATA
const formSchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('leads.fields.email') })),
    phone: zodRequiredString(tt('validation.msg.required', { field: tt('leads.fields.phone') })).refine(value => !value || phoneValidator(value), {
        message: tt('validation.msg.phone'),
    }),
});
type FormSchema = z.infer<typeof formSchema>;
const formData = reactive<FormSchema>({
    email: session.value?.user.email ?? '',
    phone: '',
});

const onSubmit = (event: FormSubmitEvent<FormSchema>): void => {
    const { data } = event;

    const body: PostLeadBodyDto = {
        ...{ [sale.sellingMethod === SellingMethod.Auction ? 'auctionId' : 'directSaleId']: sale.id },
        ...data,
        intentType,
        language: localeToLanguage(locale.value),
        urlPath: route.fullPath,
        ...(salesContact.value ? { contactSalesEmail: salesContact.value.email } : {}),
    };

    mutateSubmit({
        data: body,
    })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error posting leadData', { body, error: apiError });
        })
        .finally(() => {
            emit('close', formData);
        });
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before lead post', {
        formErrors: errorEvent.errors,
        formData,
    });
};
</script>

<template>
    <UModal v-model:open="open" :fullscreen="isMobile" :transition="!isMobile" :title="tt('leads.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <p class="text-center">{{ tt('leads.description') }}</p>

            <UForm class="flex flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
                <UFormField v-if="!session?.user.email" class="mb-2 w-full" :label="tt('login.email')" name="email" required>
                    <UInput
                        v-model.trim="formData.email"
                        autocomplete="username"
                        type="email"
                        inputmode="email"
                        :disabled="isSubmitting"
                        :autofocus="isLgOrGreater"
                    />
                </UFormField>

                <UFormField :label="tt('leads.fields.phone')" name="phone" required>
                    <template #hint>
                        <BasePhoneHint />
                    </template>
                    <BasePhoneInput v-model.trim="formData.phone" :disabled="isSubmitting" />
                </UFormField>

                <UButton :label="tt('leads.cta')" class="mt-4" type="submit" size="xl" :loading="isSubmitting" block />
            </UForm>
            <BaseI18nT tag="p" class="mt-4 text-center" keypath="leads.privacy.base">
                <template #link>
                    <NuxtLinkLocale class="text-primary font-semibold underline" :to="STORYBLOK_ROUTES.PRIVACY_POLICY" target="_blank">
                        {{ tt('leads.privacy.link') }}
                    </NuxtLinkLocale>
                </template>
            </BaseI18nT>
        </template>
    </UModal>
</template>
